import { useState, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { Avatar } from "@/components/common/Avatar";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SearchInput } from "@/components/common/SearchInput";
import { dateFmt } from "@/lib/format";
import { PRIORITY_DOT } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Users, ChevronDown, ChevronRight, Download } from "lucide-react";

export default function Workload() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const { data: workload = [], isLoading } = useQuery({ queryKey: ["pm-workload"], queryFn: () => get("/pm/workload") });

  const exportCsv = () => {
    const rows = [["Employee", "Open Items", "Points", "Overdue"], ...workload.map((w) => [w.name, w.total_items, w.total_points, w.overdue])];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "workload.csv";
    a.click();
  };

  const visible = useMemo(() => workload.filter((w) => w.total_items > 0), [workload]);
  const filteredWorkload = useMemo(() => {
    if (!search) return visible;
    return visible.filter((w) => w.name?.toLowerCase().includes(search.toLowerCase()));
  }, [visible, search]);

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Workload" subtitle="Assigned tickets and points per employee across all projects.">
        <SearchInput testid="workload-search" placeholder="Search employee" value={search} onChange={setSearch} />
        <button onClick={exportCsv} data-testid="export-workload" className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-sm hover:bg-muted">
          <Download className="h-4 w-4" />Export CSV
        </button>
      </PageHeader>

      {visible.length === 0 ? (
        <EmptyState icon={Users} title="No assigned work" />
      ) : filteredWorkload.length === 0 ? (
        <EmptyState icon={Users} title="No employees match your search" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="workload-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="w-8"></th>
                <th className="px-4 py-3 font-semibold">Employee</th>
                <th className="px-4 py-3 text-right font-semibold">Open Items</th>
                <th className="px-4 py-3 text-right font-semibold">Points</th>
                <th className="px-4 py-3 text-right font-semibold">Overdue</th>
              </tr>
            </thead>
            <tbody>
              {filteredWorkload.map((w) => (
                <Fragment key={w.user_id}>
                  <tr key={w.user_id} data-testid={`workload-row-${w.user_id}`} className="cursor-pointer border-b border-border hover:bg-muted/50"
                    onClick={() => setExpanded((e) => ({ ...e, [w.user_id]: !e[w.user_id] }))}>
                    <td className="pl-3">{expanded[w.user_id] ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}</td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><Avatar name={w.name} size="sm" color="#64748B" /><span className="font-medium">{w.name}</span></div></td>
                    <td className="px-4 py-3 text-right">{w.total_items}</td>
                    <td className="px-4 py-3 text-right font-mono">{w.total_points}</td>
                    <td className="px-4 py-3 text-right">{w.overdue > 0 ? <StatusBadge family="red" label={w.overdue} /> : <span className="text-muted-foreground">0</span>}</td>
                  </tr>
                  {expanded[w.user_id] && (
                    <tr key={`${w.user_id}-detail`}>
                      <td colSpan={5} className="bg-muted/30 px-4 py-2">
                        <div className="ml-8 space-y-1">
                          {w.pending_items.map((it) => (
                            <button key={it.id} onClick={() => navigate(`/pm/projects/${it.project_id}/items/${it.id}`)}
                              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-card">
                              <span className={cn("h-2 w-2 rounded-full", PRIORITY_DOT[it.priority])} />
                              <span className="flex-1">{it.title}</span>
                              {it.due_date && <span className="text-xs text-muted-foreground">{dateFmt(it.due_date)}</span>}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
